import { getNotificationSettings } from "../utils/api";
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

export const DEFAULT_NOTIFICATION_SETTINGS = {
  notifications_enabled: true,
  show_check_in: true,
  show_check_out: true,
  show_possible_match: true,
  show_unmatched: true,
  in_notifications_enabled: true,
  out_notifications_enabled: true,
  show_check_in_in: true,
  show_check_in_out: true,
  show_check_out_in: true,
  show_check_out_out: true,
  show_possible_match_in: true,
  show_possible_match_out: true,
  show_unmatched_in: true,
  show_unmatched_out: true,
  toast_duration_seconds: null,
};

export function useNotificationSetting() {
  const [notificationSettings, setNotificationSettingsState] = useState(
    DEFAULT_NOTIFICATION_SETTINGS,
  );
  const [notificationSettingsLoaded, setNotificationSettingsLoaded] =
    useState(false);
  const inFlightRequestRef = useRef(null);

  const setNotificationSettings = useCallback((next) => {
    setNotificationSettingsState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      return { ...DEFAULT_NOTIFICATION_SETTINGS, ...(resolved || {}) };
    });
  }, []);

  // Single shared request so every consumer observes the same settings state.
  const loadNotificationSettings = useCallback(async () => {
    if (inFlightRequestRef.current) return inFlightRequestRef.current;

    const request = getNotificationSettings()
      .then((data) => {
        const next = {
          ...DEFAULT_NOTIFICATION_SETTINGS,
          ...(data?.settings || {}),
        };
        setNotificationSettings(next);
        return next;
      })
      .finally(() => {
        inFlightRequestRef.current = null;
        setNotificationSettingsLoaded(true);
      });

    inFlightRequestRef.current = request;
    return request;
  }, [setNotificationSettings]);

  const context = useMemo(
    () => ({
      notificationSettings,
      notificationSettingsLoaded,
      setNotificationSettings,
      loadNotificationSettings,
    }),
    [
      notificationSettings,
      notificationSettingsLoaded,
      setNotificationSettings,
      loadNotificationSettings,
    ],
  );

  return context;
}
